import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useUserContext } from '../ProtectedRoute/userProvider';
import { getEnabledProviders, validateAuthConfig } from '../../services/auth/config';
import { AuthManager } from '../../services/auth/authManager';
import { AUTH_CONFIG } from '../../services/auth/config';

interface SignInModalProps {
  open: boolean;
  onClose: () => void;
}

const providerConfigs = {
  google: {
    name: 'Google',
    icon: GoogleIcon,
    color: '#4285F4',
    backgroundColor: '#4285F4',
    textColor: '#FFFFFF'
  },
  facebook: {
    name: 'Facebook',
    icon: FacebookIcon,
    color: '#1877F2',
    backgroundColor: '#1877F2',
    textColor: '#FFFFFF'
  },
  emailPassword: {
    name: 'Email',
    icon: EmailIcon,
    color: '#1976d2',
    backgroundColor: '#1976d2',
    textColor: '#FFFFFF'
  }
};

export const SignInModal: React.FC<SignInModalProps> = ({ open, onClose }) => {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { signInWithProvider, setUser } = useUserContext();
  const enabledProviders = getEnabledProviders();
  const configValidation = validateAuthConfig();
  const authManager = new AuthManager(AUTH_CONFIG);

  const handleSignIn = async (providerName: string) => {
    setLoadingProvider(providerName);
    setError(null);
    
    try {
      await signInWithProvider(providerName);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
      setError(errorMessage);
      console.error('Sign-in error:', err);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleEmailPasswordSignIn = async () => {
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoadingProvider('emailPassword');
    setError(null);

    try {
      const emailPasswordProvider = authManager.getEmailPasswordProvider();
      if (!emailPasswordProvider) {
        throw new Error('Email/password authentication is not available');
      }
      const user = await emailPasswordProvider.signIn(email, password);
      if (user) {
        setUser(user);
        onClose();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign-in failed. Please try again.';
      setError(errorMessage);
      console.error('Email/password sign-in error:', err);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleEmailPasswordSignUp = async () => {
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoadingProvider('emailPassword');
    setError(null);

    try {
      const emailPasswordProvider = authManager.getEmailPasswordProvider();
      if (!emailPasswordProvider) {
        throw new Error('Email/password authentication is not available');
      }
      const user = await emailPasswordProvider.signUp(email, password);
      if (user) {
        setUser(user);
        onClose();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign-up failed. Please try again.';
      setError(errorMessage);
      console.error('Email/password sign-up error:', err);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  // Filter out emailPassword from social providers since it has its own tab
  const socialProviders = enabledProviders.filter(provider => provider !== 'emailPassword');
  const hasEmailPassword = enabledProviders.includes('emailPassword');

  // Set initial tab to social login (tab 1) if both email/password and social providers are available
  React.useEffect(() => {
    if (hasEmailPassword && socialProviders.length > 0) {
      setTabValue(1); // Social login tab
    }
  }, [hasEmailPassword, socialProviders.length]);

  if (enabledProviders.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Sign In</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            No authentication providers are currently configured.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please check your environment configuration and ensure at least one authentication provider is enabled.
          </Typography>
          
          {!configValidation.valid && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Configuration Issues:
              </Typography>
              {configValidation.errors.map((error, index) => (
                <Typography key={index} variant="body2" component="div">
                  • {error}
                </Typography>
              ))}
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary">
            Run <code>npm run setup-env</code> to configure the environment variables.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" align="center">
          Sign In to Your Account
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            Choose your preferred sign-in method
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tabs for different authentication methods */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} centered>
            {hasEmailPassword && <Tab label="Email & Password" />}
            {socialProviders.length > 0 && <Tab label="Social Login" />}
          </Tabs>
        </Box>

        {/* Email/Password Tab */}
        {hasEmailPassword && tabValue === 0 && (
          <Box>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleEmailPasswordSignIn}
                disabled={loadingProvider === 'emailPassword'}
                startIcon={loadingProvider === 'emailPassword' ? <CircularProgress size={20} /> : <EmailIcon />}
              >
                {loadingProvider === 'emailPassword' ? 'Signing in...' : 'Sign In'}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleEmailPasswordSignUp}
                disabled={loadingProvider === 'emailPassword'}
              >
                Sign Up
              </Button>
            </Box>
            {tabValue === 0 && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  helperText="Required for sign up"
                />
              </Box>
            )}
          </Box>
        )}

        {/* Social Login Tab */}
        {socialProviders.length > 0 && (
          <Box>
            {tabValue === (hasEmailPassword ? 1 : 0) && (
              <Grid container spacing={2}>
                {socialProviders.map((providerName) => {
                  const config = providerConfigs[providerName as keyof typeof providerConfigs];
                  if (!config) {
                    console.warn(`Provider configuration not found for: ${providerName}`);
                    return null;
                  }
                  const IconComponent = config.icon;
                  
                  return (
                    <Grid item xs={12} key={providerName}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleSignIn(providerName)}
                        disabled={loadingProvider === providerName}
                        startIcon={loadingProvider === providerName ? <CircularProgress size={20} /> : <IconComponent />}
                        sx={{
                          backgroundColor: config.backgroundColor,
                          color: config.textColor,
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: config.color,
                            opacity: 0.9
                          },
                          '&:disabled': {
                            backgroundColor: config.backgroundColor,
                            opacity: 0.6
                          }
                        }}
                      >
                        {loadingProvider === providerName ? 'Signing in...' : `Continue with ${config.name}`}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 